import React, { useEffect, useRef, useState } from 'react'
import { Select } from 'antd'
import { getTemplateFormFields } from '../templateForm'
import styles from './style.module.scss'

// 가져올 형식 줄(spec D6): 파일 헤더로 판별한 템플릿을 알리기만 하고, [변경] 을 눌러야 다른 템플릿을 고르는 목록이 열린다
const TemplateImportAsLine = ({
  templateCatalog,
  selectedTemplateId,
  templateDetection,
  isParsing,
  dispatch,
  t,
}) => {
  const [isChangingTemplate, setIsChangingTemplate] = useState(false)
  const boxRef = useRef(null)

  // 선택도 판별 결과도 아직 없으면 판별 중이다 — 줄과 [변경] 을 숨겨 늦게 끝난 판별이 사용자의 선택을 덮어쓰지
  // 않게 한다(spec D6)
  const isDetecting = selectedTemplateId === null && templateDetection === null

  // 파일을 고를 때마다 판별이 다시 시작된다(파일 이름이 같아도) — SET_FILE 이 선택·판별을 비운다
  // (importReducer.js:261-268). 그때 [변경] 으로 연 목록을 닫는다
  useEffect(() => {
    if (isDetecting) setIsChangingTemplate(false)
  }, [isDetecting])

  // 안내가 생기거나 바뀌면 보이는 영역 안으로 가져온다 — 본문만 스크롤되는 고정 높이 모달이라 업로드 영역 아래의 안내는
  // 화면 밖에 남는다(spec M7·D13). jsdom 에는 scrollIntoView 가 없을 수 있어 ?.() 로 호출한다
  useEffect(() => {
    if (isDetecting) return
    boxRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [isDetecting, selectedTemplateId, templateDetection, isChangingTemplate])

  if (isDetecting) return null

  const selectedTemplate =
    templateCatalog.items.find((item) => item.id === selectedTemplateId) || null
  // 위에서부터 처음 맞는 것(중첩 삼항 금지)
  const getMessage = () => {
    if (selectedTemplateId === null) return t('record.import.v2.template.noDefault')
    if (templateDetection === 'fallback') return t('record.import.v2.template.importAsFallback')
    return t('record.import.v2.template.importAs', { name: selectedTemplate?.displayName })
  }
  // Default 도 못 찾아 선택이 비었거나 [변경] 을 눌렀을 때만 가져올 템플릿 목록을 연다
  const isSelectOpen = isChangingTemplate || selectedTemplateId === null

  const handleChange = (templateId) => {
    dispatch({ type: 'SELECT_TEMPLATE', payload: { id: templateId, detection: null } })
    setIsChangingTemplate(false)
  }

  return (
    <div ref={boxRef} className={[styles.templateForm, styles.importAsBox].join(' ')}>
      <p className={styles.importAsMessage}>
        <span>{getMessage()}</span>
        {!isSelectOpen && (
          <button
            type="button"
            className={styles.changeBtn}
            disabled={isParsing}
            onClick={() => setIsChangingTemplate(true)}
          >
            {t('record.import.v2.template.change')}
          </button>
        )}
      </p>
      {selectedTemplate && (
        <p className={styles.importAsFields}>
          {t('record.import.v2.template.importAsFields', {
            fields: getTemplateFormFields(selectedTemplate.schema)
              .map((field) => field.labelLocalized)
              .join('·'),
          })}
        </p>
      )}
      {isSelectOpen && (
        <div className={styles.selectRow}>
          <Select
            className={styles.select}
            value={selectedTemplateId}
            onChange={handleChange}
            options={templateCatalog.items.map((item) => ({
              value: item.id,
              label: item.displayName,
            }))}
            disabled={isParsing}
            placeholder={t('record.import.v2.template.selectPlaceholder')}
          />
        </div>
      )}
    </div>
  )
}

export default TemplateImportAsLine
